import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { CircleNotch, Sparkle } from "@phosphor-icons/react";
import { Button, Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { motion } from "framer-motion";
import { useCallback, useState } from "react";

import { toast } from "../hooks/use-toast";
import { useTakingLonger } from "../hooks/use-taking-longer";
import { processAi, reportAiOutcome } from "../services/ai";
import { useAiStore } from "../stores/ai";
import { useResumeStore } from "../stores/resume";

type Props = {
  sectionId: string;
  className?: string;
};

const defaultUrl = { label: "", href: "" };

const sectionFieldDefaults: Record<string, Record<string, unknown>> = {
  experience: { company: "", position: "", date: "", location: "", summary: "", url: defaultUrl },
  education: { institution: "", studyType: "", area: "", score: "", date: "", summary: "", url: defaultUrl },
  skills: { name: "", description: "", level: 1, keywords: [] },
  projects: { name: "", description: "", date: "", summary: "", keywords: [], url: defaultUrl },
  certifications: { name: "", issuer: "", date: "", summary: "", url: defaultUrl },
  awards: { title: "", awarder: "", date: "", summary: "", url: defaultUrl },
  volunteer: { organization: "", position: "", date: "", location: "", summary: "", url: defaultUrl },
  publications: { name: "", publisher: "", date: "", summary: "", url: defaultUrl },
  references: { name: "", description: "", summary: "", url: defaultUrl },
  languages: { name: "", description: "", level: 1 },
  interests: { name: "", keywords: [] },
  profiles: { network: "", username: "", icon: "", url: defaultUrl },
};

function normalizeUrl(value: unknown): { label: string; href: string } {
  if (value && typeof value === "object" && "href" in (value as Record<string, unknown>)) {
    const obj = value as Record<string, unknown>;
    return {
      label: typeof obj.label === "string" ? obj.label : "",
      href: typeof obj.href === "string" ? obj.href : "",
    };
  }
  if (typeof value === "string" && value) {
    return { label: "", href: value };
  }
  return defaultUrl;
}

function hydrateItem(raw: Record<string, unknown>, sectionId: string): Record<string, unknown> {
  const baseSectionId = sectionId.startsWith("custom.") ? "custom" : sectionId;
  const defaults = sectionFieldDefaults[baseSectionId] ?? sectionFieldDefaults.experience;

  const item: Record<string, unknown> = {
    ...defaults,
    id: createId(),
    visible: true,
  };

  for (const [key, value] of Object.entries(raw)) {
    if (key === "id" || key === "visible") continue;

    if (key === "url") {
      item.url = normalizeUrl(value);
    } else if (key === "keywords" && Array.isArray(value)) {
      item.keywords = value.filter((v) => typeof v === "string");
    } else if (key === "level" && typeof value === "number") {
      item.level = Math.min(5, Math.max(0, Math.round(value)));
    } else if (typeof value === "string") {
      item[key] = value;
    } else if (typeof value === "number") {
      item[key] = value;
    } else if (Array.isArray(value)) {
      item[key] = value.filter((v) => typeof v === "string" || typeof v === "number");
    }
  }

  return item;
}

export const AiGenerateSection = ({ sectionId, className }: Props) => {
  const [loading, setLoading] = useState(false);
  const takingLonger = useTakingLonger(loading);
  const resumeData = useResumeStore((s) => s.resume.data);
  const setValue = useResumeStore((s) => s.setValue);
  const setRateLimit = useAiStore((s) => s.setRateLimit);

  const handleGenerate = useCallback(async () => {
    if (loading) return;

    setLoading(true);

    try {
      const response = await processAi({
        action: "generate-section",
        sectionType: sectionId,
        context: { resumeData },
      });

      if (response.rateLimit) {
        setRateLimit(response.rateLimit);
      }

      if (response.ok === false) {
        toast({
          variant: "error",
          title: t`Please try again`,
          description: response.result,
        });
        return;
      }

      const items = response.metadata?.items as Array<Record<string, unknown>> | undefined;

      if (items && Array.isArray(items) && items.length > 0) {
        const hydrated = items.map((raw) => hydrateItem(raw, sectionId));

        const sectionPath = `sections.${sectionId}`;
        const sections = resumeData.sections as Record<string, { items?: unknown[] }>;
        const resolvedSection = sectionId.startsWith("custom.")
          ? (sections.custom as Record<string, { items?: unknown[] }>)?.[sectionId.replace("custom.", "")]
          : sections[sectionId];
        const currentItems = resolvedSection?.items ?? [];
        setValue(`${sectionPath}.items`, [...currentItems, ...hydrated]);
        reportAiOutcome(response.callId, "used");

        toast({
          variant: "success",
          title: t`Content generated`,
          description: t`${String(hydrated.length)} item(s) added. Review and customize them to match your experience.`,
        });
      } else {
        toast({
          variant: "info",
          title: t`Nothing to add yet`,
          description: t`Add a real role or details first. AI will not invent employers, schools, or metrics.`,
        });
      }

      if (response.rateLimit?.warning) {
        toast({
          variant: "warning",
          title: t`Usage notice`,
          description: response.rateLimit.warning,
        });
      }
    } catch (error) {
      toast({
        variant: "error",
        title: t`Generation failed`,
        description: (error as Error)?.message,
      });
    } finally {
      setLoading(false);
    }
  }, [loading, sectionId, resumeData, setValue, setRateLimit]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className={className}
    >
      <Tooltip content={t`Let AI draft content based on the rest of your resume`}>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleGenerate}
          disabled={loading}
          className={cn(
            "w-full gap-2 border border-dashed border-primary/20 py-3 text-xs",
            "bg-gradient-to-r from-primary/[0.02] to-primary/[0.05]",
            "text-primary/70 transition-all hover:border-primary/30 hover:bg-primary/[0.08] hover:text-primary",
          )}
        >
          {loading ? (
            <>
              <CircleNotch size={13} className="animate-spin" />
              <span>{takingLonger ? t`Taking longer than usual…` : t`Generating...`}</span>
            </>
          ) : (
            <>
              <Sparkle size={13} weight="duotone" />
              <span>{t`Generate with AI`}</span>
            </>
          )}
        </Button>
      </Tooltip>
    </motion.div>
  );
};
